// src/auth/ProtectedRoute.jsx
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

import { useAuth } from "./AuthProvider";
import { useAppLoading } from "../context/AppLoadingContext";

export default function ProtectedRoute({
  children,
  loadingTitle,
  loadingSubtitle,
  loadingStyle,
  loadingBg,
  allowDisclaimerPending = false,
}) {
  const {
    user,
    authLoading,
    blockedReason,
    blockedMessage,
    mustAcceptCurrentDisclaimer,
  } = useAuth();
  const { showOverlay, hideOverlay } = useAppLoading();

  const location = useLocation();
  const nav = useNavigate();

  const handledRef = useRef(false);

  // useLayoutEffect runs children-before-parents, so the child page's showOverlay
  // increments the count before our cleanup decrements it — overlay never hits 0
  useLayoutEffect(() => {
    if (authLoading || !user || blockedReason) {
      const suppressClientsLogoutLoader =
        location.pathname === "/clients" &&
        sessionStorage.getItem("mykyc:suppressClientsLogoutLoader") === "1";
      const suppressLogoutLoader = sessionStorage.getItem("mykyc:loggedOut") === "1";
      const suppress = suppressClientsLogoutLoader || suppressLogoutLoader;

      showOverlay({
        title: loadingTitle ?? "Signing you in…",
        subtitle: loadingSubtitle ?? "Checking authentication and access permissions",
        loaderStyle: loadingStyle ?? "spinner",
        bg: suppress ? "transparent" : loadingBg,
        hideContent: suppress,
      });
      return () => hideOverlay();
    }
  }, [authLoading, user, blockedReason, loadingTitle, loadingSubtitle, loadingStyle, loadingBg, location.pathname, showOverlay, hideOverlay]);

  useEffect(() => {
    // reset guard when auth is loading again (fresh session / refresh)
    if (authLoading) {
      handledRef.current = false;
      return;
    }

    // 1) Not logged in -> go login
    if (!user) {
      if (handledRef.current) return;
      handledRef.current = true;

      nav("/login", { replace: true, state: { from: location } });
      return;
    }

    // 2) Logged in BUT blocked (user disabled / org disabled / etc.)
    if (blockedReason) {
      if (handledRef.current) return;
      handledRef.current = true;

      (async () => {
        await signOut(auth).catch(() => {});
        nav("/login", {
          replace: true,
          state: {
            authError: blockedMessage || "Access blocked.",
            from: location,
            toastId: Date.now(),
          },
        });
      })();

      return;
    }

    if (mustAcceptCurrentDisclaimer && !allowDisclaimerPending) {
      if (handledRef.current) return;
      handledRef.current = true;
      nav("/accept-disclaimer", { replace: true, state: { from: location } });
    }
  }, [
    authLoading,
    user,
    blockedReason,
    blockedMessage,
    mustAcceptCurrentDisclaimer,
    allowDisclaimerPending,
    nav,
    location,
  ]);

  // While auth is pending or user is absent/blocked -> render nothing (overlay via context)
  if (
    authLoading ||
    !user ||
    blockedReason ||
    (mustAcceptCurrentDisclaimer && !allowDisclaimerPending)
  ) return null;

  return children;
}
